import { useMutation } from '@tanstack/react-query';

import {
  useInvalidateChannelById,
  useInvalidateChannelsList,
} from '@/entities/channel';
import { ApiError } from '@/shared/api/ApiError';
import {
  connectOzon,
  type OzonConnectResponse,
} from '@/shared/api/channels/ozon';
import { useNotifications } from '@/shared/ui/Notifications';

interface OzonConnectInput {
  channelId: string;
  clientId: string;
  apiKey: string;
  name?: string;
}

export function useOzonConnect() {
  const invalidateChannelsList = useInvalidateChannelsList();
  const invalidateChannelById = useInvalidateChannelById();
  const { addNotification } = useNotifications();

  const mutation = useMutation<OzonConnectResponse, ApiError, OzonConnectInput>({
    mutationFn: ({ channelId, clientId, apiKey, name }) =>
      connectOzon(channelId, {
        client_id: clientId,
        api_key: apiKey,
        name: name?.trim() || undefined,
      }),
    onSuccess: async (_data, variables) => {
      await Promise.all([
        invalidateChannelsList(),
        invalidateChannelById(variables.channelId),
      ]);
    },
    onError: (error) => {
      addNotification({
        title: error.message,
        caption: error?.data?.details,
      });
    },
  });

  return {
    connect: (input: OzonConnectInput) => mutation.mutateAsync(input),
    isPending: mutation.isPending,
    reset: mutation.reset,
  };
}
